import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasActiveSession } from '../../utils/apiClient';
import { isSafeInternalPath } from '../../utils/authRedirect';

type AuthState = 'checking' | 'authenticated' | 'unauthenticated';

type Props = {
  children: ReactNode;
};

/**
 * Protects the product route tree using the backend session cookie.
 *
 * The check runs once when the protected route boundary mounts. Subsequent
 * API responses remain responsible for handling an expired session.
 */
export function AuthGuard({ children }: Props) {
  const location = useLocation();
  const [authState, setAuthState] = useState<AuthState>('checking');

  useEffect(() => {
    let cancelled = false;

    hasActiveSession().then(active => {
      if (cancelled) return;
      setAuthState(active ? 'authenticated' : 'unauthenticated');
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (authState === 'checking') return null;

  if (authState === 'unauthenticated') {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const params = new URLSearchParams();
    if (isSafeInternalPath(returnTo)) {
      params.set('return_to', returnTo);
    }

    return (
      <Navigate
        to={{
          pathname: '/login',
          search: params.toString() ? `?${params.toString()}` : '',
        }}
        replace
      />
    );
  }

  return <>{children}</>;
}
